import { Injectable, inject, type ResourceRef, type Signal } from '@angular/core';
import { HttpClient, HttpHeaders, httpResource, type HttpResourceRef } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { firstValueFrom, map, of, tap } from 'rxjs';
import { API_BASE_URL, silentErrors } from '@shared/http';
import { type Resettable } from '@shared/config/app-reset.service';
import {
  BuildSwapResultSchema,
  ConfirmSwapSchema,
  CrossChainBuildSchema,
  CrossChainQuoteSchema,
  CrossChainSubmitSchema,
  CrossChainSwapSchema,
  PriceSchema,
  QuoteSchema,
  TradeHistorySchema,
  quoteKey,
  type BuildSwapResult,
  type ConfirmSwap,
  type CrossChainBuild,
  type CrossChainQuote,
  type CrossChainQuoteParams,
  type CrossChainSubmit,
  type CrossChainSwap,
  type ExecuteSwapParams,
  type Price,
  type PriceParams,
  type Quote,
  type QuoteParams,
  type TradeHistory,
} from '../model/trading.schema';

const QUOTE_CACHE_TTL_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class TradingApi implements Resettable {
  private readonly base = inject(API_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly quoteCache = new Map<string, { value: Quote; ts: number }>();

  private quoteUrl(p: QuoteParams): string {
    const q = new URLSearchParams({
      fromToken: p.fromToken,
      toToken: p.toToken,
      amount: p.amount,
      network: p.network,
    });
    if (p.slippageBps !== undefined) q.set('slippageBps', String(p.slippageBps));
    return `${this.base}/me/trading/quote?${q.toString()}`;
  }

  private cachedQuote(p: QuoteParams): Quote | null {
    const c = this.quoteCache.get(quoteKey(p));
    return c && Date.now() - c.ts < QUOTE_CACHE_TTL_MS ? c.value : null;
  }

  private storeQuote(p: QuoteParams, value: Quote): void {
    const now = Date.now();
    for (const [k, v] of this.quoteCache) {
      if (now - v.ts >= QUOTE_CACHE_TTL_MS) this.quoteCache.delete(k);
    }
    this.quoteCache.set(quoteKey(p), { value, ts: now });
  }

  invalidateQuote(p: QuoteParams): void {
    this.quoteCache.delete(quoteKey(p));
  }

  reset(): void {
    this.quoteCache.clear();
  }

  async quoteOnce(params: QuoteParams): Promise<Quote> {
    const cached = this.cachedQuote(params);
    if (cached) return cached;
    const raw = await firstValueFrom(this.http.get<unknown>(this.quoteUrl(params)));
    const value = QuoteSchema.parse(raw);
    this.storeQuote(params, value);
    return value;
  }

  quote(params: Signal<QuoteParams | null>): ResourceRef<Quote | undefined> {
    return rxResource({
      params: () => params() ?? undefined,
      stream: ({ params: p }) => {
        const cached = this.cachedQuote(p);
        if (cached) return of(cached);
        return this.http.get<unknown>(this.quoteUrl(p), { context: silentErrors() }).pipe(
          map(raw => QuoteSchema.parse(raw)),
          tap(value => this.storeQuote(p, value)),
        );
      },
    });
  }

  history(): HttpResourceRef<TradeHistory | undefined> {
    return httpResource<TradeHistory>(() => `${this.base}/me/trading/history?limit=20`, {
      parse: TradeHistorySchema.parse,
    });
  }

  price(params: Signal<PriceParams | undefined>): ResourceRef<Price | undefined> {
    return rxResource({
      params: () => params(),
      stream: ({ params: p }) =>
        this.http
          .post<unknown>(`${this.base}/me/trading/price`, p)
          .pipe(map(raw => PriceSchema.parse(raw))),
    });
  }

  async buildSwap(params: ExecuteSwapParams): Promise<BuildSwapResult> {
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/me/trading/swap`, params, { context: silentErrors() }),
    );
    return BuildSwapResultSchema.parse(raw);
  }

  async submitSwap(swapId: string, freshToken: string): Promise<ConfirmSwap> {
    const raw = await firstValueFrom(
      this.http.post<unknown>(
        `${this.base}/me/trading/swap/${swapId}/submit`,
        {},
        {
          headers: new HttpHeaders({ 'x-fresh-token': freshToken }),
          context: silentErrors(),
        },
      ),
    );
    return ConfirmSwapSchema.parse(raw);
  }

  crossChainQuote(
    params: Signal<CrossChainQuoteParams | null>,
  ): ResourceRef<CrossChainQuote | undefined> {
    return rxResource({
      params: () => params() ?? undefined,
      stream: ({ params: p }) => {
        const q = new URLSearchParams({
          fromNetwork: p.fromNetwork,
          toNetwork: p.toNetwork,
          fromToken: p.fromToken,
          toToken: p.toToken,
          amount: p.amount,
          userAddress: p.userAddress,
        });
        if (p.slippageBps !== undefined) q.set('slippageBps', String(p.slippageBps));
        return this.http
          .get<unknown>(`${this.base}/me/trading/crosschain/quote?${q.toString()}`, {
            context: silentErrors(),
          })
          .pipe(map(raw => CrossChainQuoteSchema.parse(raw)));
      },
    });
  }

  async crossChainBuild(
    params: CrossChainQuoteParams,
    idempotencyKey: string,
  ): Promise<CrossChainBuild> {
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/me/trading/crosschain/swap`, params, {
        headers: new HttpHeaders({ 'X-Idempotency-Key': idempotencyKey }),
        context: silentErrors(),
      }),
    );
    return CrossChainBuildSchema.parse(raw);
  }

  async crossChainSubmit(swapId: string, freshToken: string): Promise<CrossChainSubmit> {
    const raw = await firstValueFrom(
      this.http.post<unknown>(
        `${this.base}/me/trading/crosschain/swap/${swapId}/submit`,
        {},
        {
          headers: new HttpHeaders({ 'x-fresh-token': freshToken }),
          context: silentErrors(),
        },
      ),
    );
    return CrossChainSubmitSchema.parse(raw);
  }

  async crossChainStatus(swapId: string): Promise<CrossChainSwap> {
    const raw = await firstValueFrom(
      this.http.get<unknown>(`${this.base}/me/trading/crosschain/swap/${swapId}`, {
        context: silentErrors(),
      }),
    );
    return CrossChainSwapSchema.parse(raw);
  }
}
